<script setup>
import { useId } from 'vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PriorityCircle from '../../kanban/components/PriorityCircle.vue';

defineProps({
  hint: { type: String, required: true },
  funnelLabel: { type: String, required: true },
  stageLabel: { type: String, required: true },
  loadingLabel: { type: String, required: true },
  emptyLabel: { type: String, required: true },
  updatedLabel: { type: String, required: true },
  stageSinceLabel: { type: String, required: true },
  attachmentsLabel: { type: String, required: true },
  conversationLabel: { type: String, required: true },
  cardIdLabel: { type: String, required: true },
  assignButtonLabel: { type: String, required: true },
  assignButtonAria: { type: String, required: true },
  funnelId: { type: String, required: true },
  funnelOptions: { type: Array, required: true },
  stageId: { type: String, required: true },
  stageOptions: { type: Array, required: true },
  items: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  itemAriaLabel: { type: Function, required: true },
  itemMeta: { type: Function, required: true },
  onOpenItem: { type: Function, required: true },
  translate: { type: Function, required: true },
});

const emit = defineEmits(['update:funnelId', 'update:stageId', 'openAssign']);

const funnelFieldLabelId = useId();
const stageFieldLabelId = useId();

function handleFunnelChange(value) {
  emit('update:funnelId', value);
}

function handleStageChange(value) {
  emit('update:stageId', value);
}
</script>

<template>
  <div class="overflow-y-auto flex-1 flex flex-col gap-4 px-4 pb-4 min-h-0">
    <div class="flex items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="blue"
        size="md"
        icon="i-ri-layout-grid-line"
        class="w-full justify-center"
        :label="assignButtonLabel"
        :aria-label="assignButtonAria"
        @click="emit('openAssign')"
      />
    </div>
    <div class="rounded-2xl border border-n-weak bg-n-surface-1">
      <div class="px-3.5 pt-3.5 pb-2 border-b border-n-weak/80">
        <div class="flex gap-2 items-start">
          <span
            class="mt-0.5 i-ri-equalizer-line size-4 text-n-slate-10 shrink-0"
            aria-hidden="true"
          />
          <p class="mb-0 text-xs leading-snug text-n-slate-11">
            {{ hint }}
          </p>
        </div>
      </div>
      <div
        class="grid grid-cols-1 gap-4 p-3.5 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-3"
      >
        <div class="flex flex-col gap-1.5 min-w-0">
          <div
            class="flex gap-1.5 items-center text-xs font-medium text-n-slate-11"
            :id="funnelFieldLabelId"
          >
            <span
              class="i-ri-route-line size-3.5 text-n-teal-11 shrink-0"
              aria-hidden="true"
            />
            {{ funnelLabel }}
          </div>
          <div
            class="w-full min-w-0 [&>div]:!w-full [&_select]:w-full [&_select]:min-w-0"
            role="group"
            :aria-labelledby="funnelFieldLabelId"
          >
            <Select
              :model-value="funnelId"
              :options="funnelOptions"
              @update:model-value="handleFunnelChange"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1.5 min-w-0">
          <div
            class="flex gap-1.5 items-center text-xs font-medium text-n-slate-11"
            :id="stageFieldLabelId"
          >
            <span
              class="i-ri-stack-line size-3.5 text-n-iris-11 shrink-0"
              aria-hidden="true"
            />
            {{ stageLabel }}
          </div>
          <div
            class="w-full min-w-0 [&>div]:!w-full [&_select]:w-full [&_select]:min-w-0"
            role="group"
            :aria-labelledby="stageFieldLabelId"
          >
            <Select
              :model-value="stageId"
              :options="stageOptions"
              @update:model-value="handleStageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ loadingLabel }}
    </div>
    <ul
      v-else
      class="overflow-y-auto flex-1 min-h-[8rem] p-0 m-0 list-none rounded-xl border divide-y divide-n-weak border-n-weak"
    >
      <li v-if="items.length === 0" class="px-3 py-4 text-sm text-center text-n-slate-11">
        {{ emptyLabel }}
      </li>
      <li
        v-for="item in items"
        :key="item.id"
        role="button"
        tabindex="0"
        class="flex gap-3 justify-between items-start px-3 py-3 cursor-pointer transition-colors outline-none hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-alpha-3"
        :aria-label="itemAriaLabel(item)"
        @click="onOpenItem(item)"
        @keydown.enter.prevent="onOpenItem(item)"
      >
        <template v-for="meta in [itemMeta(item)]" :key="`peer-kanban-${item.id}`">
          <div class="flex flex-col flex-1 gap-1.5 min-w-0">
            <div class="flex gap-2 items-start min-w-0 w-full">
              <div class="flex flex-1 gap-2 items-start min-w-0">
                <PriorityCircle
                  v-if="meta.showPriority"
                  :priority="meta.priority"
                  :size="16"
                  class="flex-shrink-0 mt-0.5"
                />
                <span
                  class="text-sm font-medium leading-snug text-n-slate-12 line-clamp-2 min-w-0"
                >
                  {{ meta.title }}
                </span>
              </div>
              <div class="flex flex-shrink-0 items-center gap-2 self-start">
                <span
                  v-if="meta.idPart"
                  class="inline-flex items-center px-1.5 py-px text-xxs font-normal tabular-nums rounded-md bg-n-alpha-2 text-n-slate-10"
                >
                  {{ cardIdLabel }}
                  {{ meta.idPart }}
                </span>
                <div v-if="meta.assignedAgents.length" class="flex items-center gap-1">
                  <div class="flex items-center -space-x-1.5">
                    <div
                      v-for="ag in meta.assignedAgents"
                      :key="ag.id"
                      class="ring-2 ring-n-background rounded-full"
                    >
                      <Avatar
                        :name="ag.name"
                        :src="ag.avatar_url || ''"
                        :size="24"
                        rounded-full
                      />
                    </div>
                  </div>
                  <span
                    v-if="meta.assignedExtraCount > 0"
                    class="text-xxs font-medium tabular-nums text-n-slate-10"
                  >
                    +{{ meta.assignedExtraCount }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex flex-wrap gap-1.5 items-center">
              <span
                v-if="meta.stage"
                class="inline-flex items-center px-2 py-0.5 text-xxs font-medium rounded-full border leading-none max-w-[11rem] truncate"
                :style="{
                  backgroundColor: `${meta.stage.color}20`,
                  color: meta.stage.color,
                  borderColor: `${meta.stage.color}40`,
                }"
              >
                {{ meta.stage.name }}
              </span>
              <span v-if="meta.showPriority" class="text-xxs font-medium text-n-slate-11">
                {{ translate(`KANBAN.PRIORITY_LABELS.${meta.priority.toUpperCase()}`) }}
              </span>
              <span
                v-if="meta.funnelName"
                class="text-xxs text-n-slate-10 truncate max-w-[12rem]"
              >
                {{ meta.funnelName }}
              </span>
            </div>
            <div class="flex flex-wrap gap-x-3 gap-y-0.5 text-xxs text-n-slate-10">
              <span
                v-if="meta.conversationDisplayId != null && meta.conversationDisplayId !== ''"
                class="inline-flex gap-1 items-center"
              >
                <span
                  class="i-ri-chat-1-line size-3.5 shrink-0 opacity-80"
                  aria-hidden="true"
                />
                {{ conversationLabel }}
                #{{ meta.conversationDisplayId }}
              </span>
            </div>
            <p
              v-if="meta.description"
              class="mb-0 text-xxs leading-snug text-n-slate-11 line-clamp-2"
            >
              {{ meta.description }}
            </p>
            <div class="flex flex-wrap gap-x-3 gap-y-0.5 text-xxs text-n-slate-10">
              <span v-if="meta.updatedRel">
                {{ updatedLabel }}
                {{ meta.updatedRel }}
              </span>
              <span v-if="meta.stageEnteredRel">
                {{ stageSinceLabel }}
                {{ meta.stageEnteredRel }}
              </span>
              <span
                v-if="meta.attachmentsCount > 0"
                class="inline-flex gap-1 items-center"
              >
                <span
                  class="i-ri-attachment-line size-3.5 shrink-0 opacity-80"
                  aria-hidden="true"
                />
                {{ meta.attachmentsCount }}
                {{ attachmentsLabel }}
              </span>
              <span
                v-if="meta.checklistTotalCount > 0"
                class="inline-flex gap-1 items-center"
              >
                <span
                  class="i-ri-list-check size-3.5 shrink-0 opacity-80"
                  aria-hidden="true"
                />
                {{ meta.checklistOpenCount }}/{{ meta.checklistTotalCount }}
              </span>
            </div>
          </div>
        </template>
        <span
          class="flex-shrink-0 i-ri-arrow-right-s-line size-4 text-n-slate-10 mt-1"
          aria-hidden="true"
        />
      </li>
    </ul>
  </div>
</template>
